import { Check, MapPin, Phone, User } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MedicalProvider,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import { getSimpleTreatmentStatus, SimpleTreatmentStatus } from '../utils';

interface MergeStepSelectionProps {
  providers: MedicalProvider[];
  destinationId: string;
  onDestinationChange: (id: string) => void;
}

export const MergeStepSelection = ({
  providers,
  destinationId,
  onDestinationChange,
}: MergeStepSelectionProps) => {
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'NEEDS_APPROVAL':
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            Needs Approval
          </Badge>
        );
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  const getTreatmentStatus = (treatmentStatus: ProviderTreatmentStatus) => {
    const currentStatus = getSimpleTreatmentStatus(treatmentStatus);
    switch (currentStatus) {
      case SimpleTreatmentStatus.TREATING:
        return (
          <Badge className="bg-blue-100 text-blue-800">Still Treating</Badge>
        );
      case SimpleTreatmentStatus.COMPLETED:
        return (
          <Badge className="bg-gray-100 text-gray-800">Not Treating</Badge>
        );
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-medium text-gray-900 mb-2">
          Select Destination Provider
        </h2>
        <p className="text-sm text-gray-600">
          Choose which provider should remain after the merge. All information
          from other providers will be merged into this one.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {providers.map((provider) => {
          const isSelected = destinationId === provider.id;

          return (
            <Card
              key={provider.id}
              className={`cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'ring-2 ring-blue-500 bg-blue-50'
                  : 'hover:shadow-md hover:bg-gray-50'
              }`}
              onClick={() => onDestinationChange(provider.id!)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-medium text-gray-900">
                    {provider.name || 'Unnamed Provider'}
                  </CardTitle>
                  {isSelected && (
                    <div className="flex-shrink-0 ml-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-blue-600 rounded-full">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {getStatusBadge(provider.status)}
                  {getTreatmentStatus(provider.treatment_status)}
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="space-y-3">
                  {provider.address && (
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <span className="text-sm text-gray-600">
                        {provider.address}
                      </span>
                    </div>
                  )}

                  {provider.practitioner_names &&
                    provider.practitioner_names.length > 0 && (
                      <div className="flex items-start space-x-2">
                        <User className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="text-sm text-gray-600">
                          {provider.practitioner_names.join(', ')}
                        </div>
                      </div>
                    )}

                  {provider.handles && provider.handles.length > 0 && (
                    <div className="flex items-start space-x-2">
                      <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div className="text-sm text-gray-600">
                        {provider.handles.length} contact
                        {provider.handles.length === 1 ? '' : 's'}
                      </div>
                    </div>
                  )}

                  {provider.encounters && provider.encounters.length > 0 && (
                    <div className="text-sm text-gray-600">
                      {provider.encounters.length} encounter
                      {provider.encounters.length === 1 ? '' : 's'}
                    </div>
                  )}

                  {provider.description && (
                    <div className="text-sm text-gray-600 line-clamp-2">
                      {provider.description}
                    </div>
                  )}
                </div>

                {isSelected && (
                  <div className="mt-4 pt-3 border-t border-blue-200">
                    <Button
                      size="sm"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDestinationChange(provider.id!);
                      }}
                    >
                      Selected as Destination
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {destinationId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Selection Summary</h3>
          <p className="text-sm text-blue-800">
            <strong>
              {providers.find((p) => p.id === destinationId)?.name}
            </strong>{' '}
            will be the destination provider. All other selected providers will
            be merged into this one, and their data will be combined.
          </p>
        </div>
      )}
    </div>
  );
};
